import { KATHMANDU_TZ, toBsDate } from '@/lib/bsDate'
import { bsToAd, type BsParts } from '@/lib/bsToAd'

/**
 * Pure Bikram Sambat calendar-grid math for `BsCalendar` — building a month's
 * cells, walking a focused day by arrows/PageUp-PageDown, and the
 * single-day / date-range picking state machine.
 *
 * No React, no DOM: everything here takes and returns AD `YYYY-MM-DD`
 * strings (or BS year/month/day parts), which is what makes the calendar's
 * trickiest parts — month lengths that vary by year, and a range that can be
 * picked in either direction — testable without rendering anything.
 */

export type BsCalendarValue =
  | { mode: 'single'; date: string | null }
  | { mode: 'range'; start: string | null; end: string | null }

export type DayPickStatus = 'none' | 'selected' | 'range-start' | 'range-end' | 'in-range'

export interface BsCalendarCell {
  /** AD `YYYY-MM-DD`. */
  iso: string
  bsYear: number
  /** 1-12. */
  bsMonth: number
  bsDay: number
  /** 0 (Sun) – 6 (Sat), matching `Date#getDay`. */
  weekday: number
  /** True for the previous/next month's days that fill out the grid. */
  outside: boolean
}

/** The underlying table's coverage — see bsToAd.ts's doc comment. */
export const BS_MIN_YEAR = 2000
export const BS_MAX_YEAR = 2090

const pad2 = (value: number): string => String(value).padStart(2, '0')

const parseIso = (iso: string): { year: number; month: number; day: number } => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (match === null) throw new Error(`bsCalendarMath: not an ISO date: ${iso}`)
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

const toIso = (year: number, month: number, day: number): string =>
  `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`

/** Local-date construction throughout — see bsToAd.ts on why never UTC. */
const localDate = (iso: string): Date => {
  const { year, month, day } = parseIso(iso)
  return new Date(year, month - 1, day)
}

/** Today in Nepal, as an AD ISO date — the default future-day cutoff. */
export const todayAdIso = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: KATHMANDU_TZ }).format(new Date())

export const addDaysIso = (iso: string, delta: number): string => {
  const date = localDate(iso)
  date.setDate(date.getDate() + delta)
  return toIso(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

export const weekdayOfIso = (iso: string): number => localDate(iso).getDay()

/** Whole days from `startIso` to `endIso`, inclusive of both ends. */
export const countInclusiveDays = (startIso: string, endIso: string): number => {
  const a = parseIso(startIso)
  const b = parseIso(endIso)
  const start = Date.UTC(a.year, a.month - 1, a.day)
  const end = Date.UTC(b.year, b.month - 1, b.day)
  return Math.round((end - start) / 86_400_000) + 1
}

export const adToBsParts = (iso: string): BsParts | null => {
  const date = toBsDate(iso)
  if (date === null) return null
  return { year: date.getYear(), month: date.getMonth() + 1, day: date.getDate() }
}

/** `delta` can be any integer; carries years the way `Date`'s own setters do. */
export const shiftBsMonth = (
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } => {
  const zeroBased = month - 1 + delta
  const nextYear = year + Math.floor(zeroBased / 12)
  const nextMonth = ((zeroBased % 12) + 12) % 12
  return { year: nextYear, month: nextMonth + 1 }
}

/**
 * How many days BS `year`/`month` has (29-32; BS month lengths vary by year,
 * unlike AD's fixed calendar).
 *
 * Probes downward from 32 using `bsToAd`'s own round-trip validation rather
 * than a lookup table: `bsToAd` already refuses a day the library would
 * otherwise silently roll into next month (see its doc comment), so the
 * highest day that comes back non-null IS the month's length.
 */
export const daysInBsMonth = (year: number, month: number): number => {
  for (let day = 32; day >= 28; day -= 1) {
    if (bsToAd({ year, month, day }) !== null) return day
  }
  // Unreachable for any month inside the converter's BS 2000-2090 table —
  // every month it knows about has at least 29 days — but a real number
  // keeps a caller's arithmetic sane if this is ever reached.
  return 30
}

/**
 * `iso` shifted by `delta` BS months, keeping the same day-of-month where
 * that exists and clamping to the target month's last day otherwise (BS
 * 32-05-32 has no Aswin equivalent on the 32nd, so it lands on Aswin's last
 * day instead of rolling into Kartik — this is PageUp/PageDown's job, not a
 * date that silently becomes a different one three months from where the
 * member asked).
 *
 * Null when `iso` itself cannot be read as a BS date (outside the
 * converter's table) or the shifted month lands outside it.
 */
export const addBsMonths = (iso: string, delta: number): string | null => {
  const parts = adToBsParts(iso)
  if (parts === null) return null
  const target = shiftBsMonth(parts.year, parts.month, delta)
  const day = Math.min(parts.day, daysInBsMonth(target.year, target.month))
  return bsToAd({ year: target.year, month: target.month, day })
}

/**
 * The 35 or 42 cells (5 or 6 full weeks) that draw BS `year`/`month`,
 * including the previous/next month's overflow days that fill the first and
 * last rows.
 *
 * Returns `[]` only if `year`/`month` itself is outside the converter's
 * table — every cell inside a valid month is guaranteed a real `iso`.
 */
export const buildBsMonthGrid = (year: number, month: number): BsCalendarCell[] => {
  const firstIso = bsToAd({ year, month, day: 1 })
  if (firstIso === null) return []

  const firstWeekday = weekdayOfIso(firstIso)
  const monthLength = daysInBsMonth(year, month)
  const totalCells = Math.ceil((firstWeekday + monthLength) / 7) * 7

  const prev = shiftBsMonth(year, month, -1)
  const prevLength = daysInBsMonth(prev.year, prev.month)
  const next = shiftBsMonth(year, month, 1)

  const cells: BsCalendarCell[] = []
  for (let index = 0; index < totalCells; index += 1) {
    const dayOffset = index - firstWeekday
    let bsYear = year
    let bsMonth = month
    let bsDay = dayOffset + 1
    let outside = false

    if (dayOffset < 0) {
      bsYear = prev.year
      bsMonth = prev.month
      bsDay = prevLength + dayOffset + 1
      outside = true
    } else if (dayOffset >= monthLength) {
      bsYear = next.year
      bsMonth = next.month
      bsDay = dayOffset - monthLength + 1
      outside = true
    }

    const iso = bsToAd({ year: bsYear, month: bsMonth, day: bsDay })
    // Defensive: every cell above is a valid day of a real month, so this
    // never actually returns null.
    if (iso === null) continue
    cells.push({ iso, bsYear, bsMonth, bsDay, weekday: index % 7, outside })
  }
  return cells
}

/**
 * The single-day / date-range picking state machine, driven by one click (or
 * one Enter/Space) at a time:
 *
 * - single mode: the clicked day is always the new value.
 * - range mode with nothing picked, or a COMPLETE range already picked:
 *   starts a fresh range at the clicked day.
 * - range mode mid-pick (a start with no end yet): the clicked day completes
 *   it, earliest day first regardless of click order — so clicking BEFORE
 *   the start swaps rather than producing an inverted range, and clicking
 *   the start again makes a valid one-day range instead of a no-op.
 */
export const applyDayPick = (value: BsCalendarValue, iso: string): BsCalendarValue => {
  if (value.mode === 'single') return { mode: 'single', date: iso }

  const { start, end } = value
  if (start === null || end !== null) return { mode: 'range', start: iso, end: null }
  return iso < start ? { mode: 'range', start: iso, end: start } : { mode: 'range', start, end: iso }
}

/** How `iso` should be drawn, given the current selection. */
export const dayRangeStatus = (value: BsCalendarValue, iso: string): DayPickStatus => {
  if (value.mode === 'single') return value.date === iso ? 'selected' : 'none'

  const { start, end } = value
  if (start === null) return 'none'
  if (end === null) return iso === start ? 'range-start' : 'none'
  // A one-day range reads as a single selected day, not a zero-width range.
  if (iso === start && iso === end) return 'selected'
  if (iso === start) return 'range-start'
  if (iso === end) return 'range-end'
  return iso > start && iso < end ? 'in-range' : 'none'
}
